import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { erpApi, type ErpAgent } from '../../api/erp';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { formatCount, formatMoney, toCount } from '../../utils/numbers';
import './erp.css';

function statusBadge(status: string) {
  if (status === 'active') return <Badge tone="success">Activo</Badge>;
  if (status === 'suspended') return <Badge tone="warning">Suspendido</Badge>;
  return <Badge>Inactivo</Badge>;
}

function pendingSummary(agent: ErpAgent) {
  const earnings = Number(agent.pending_earnings_total ?? 0);
  const payoutCount = toCount(agent.pending_payout_count);
  const payoutAmount = Number(agent.pending_payout_amount ?? 0);

  if (earnings <= 0 && payoutCount <= 0) {
    return <span className="muted">—</span>;
  }

  return (
    <div className="erp-agent-pending">
      {earnings > 0 ? (
        <span className="erp-agent-pending__earnings">{formatMoney(earnings)} sin liquidar</span>
      ) : null}
      {payoutCount > 0 ? (
        <span className="erp-agent-pending__payout">
          {formatCount(payoutCount)} liq. · {formatMoney(payoutAmount)} por pagar
        </span>
      ) : null}
    </div>
  );
}

export function ErpAgentsPage() {
  const [agents, setAgents] = useState<ErpAgent[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    erpApi
      .listAgents()
      .then((res) => setAgents(res.agents))
      .catch((err) => setError(err instanceof Error ? err.message : 'Error al cargar'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h1 style={{ margin: 0 }}>Agentes</h1>
        <Link to="/erp/agentes/nuevo">
          <Button size="sm">Nuevo agente</Button>
        </Link>
      </div>

      {loading ? <p>Cargando…</p> : null}
      {error ? <p style={{ color: 'var(--color-danger)' }}>{error}</p> : null}

      <div className="erp-table-wrap">
        <table className="erp-table">
          <thead>
            <tr>
              <th>Agente</th>
              <th>Estado</th>
              <th>Nivel</th>
              <th>Tickets</th>
              <th>Ventas</th>
              <th>Pendientes</th>
              <th>Referido</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {agents.map((agent) => (
              <tr key={agent.id}>
                <td>
                  <strong>
                    {agent.name} {agent.surname}
                  </strong>
                  <br />
                  <span className="muted" style={{ fontSize: '0.85rem' }}>
                    {agent.email}
                  </span>
                </td>
                <td>{statusBadge(agent.status)}</td>
                <td style={{ textTransform: 'capitalize' }}>{agent.current_level}</td>
                <td>
                  {formatCount(agent.total_tickets_resolved)} resueltos
                  {toCount(agent.open_tickets) > 0 ? ` · ${formatCount(agent.open_tickets)} abiertos` : ''}
                </td>
                <td>{formatCount(agent.total_sales_made)}</td>
                <td>{pendingSummary(agent)}</td>
                <td>
                  <code style={{ fontSize: '0.8rem' }}>{agent.referral_code ?? '—'}</code>
                </td>
                <td>
                  <Link to={`/erp/agentes/${agent.id}`}>
                    <Button size="sm" variant="ghost">
                      Ver
                    </Button>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
